import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Button,
  useTheme,
} from '@mui/material'
import { keyframes } from '@mui/system'
import { PulseIndicator } from '../pulse-indicator'
import { CurrentStep } from './utils'
import { CompanyOpeningCardStatus } from '@/types'

type CompanyOpeningStatusAlertProps = {
  currentStep: CurrentStep
  cardStatus: CompanyOpeningCardStatus
  onOpenDialog: () => void
}

const CARD_STATUS_TITLE: Record<CompanyOpeningCardStatus, string> = {
  in_progress: 'Abertura da PJ em andamento',
  completed: 'Abertura da PJ concluída!',
  completed_council_pending:
    'Abertura concluída - Registro no Conselho Pendente',
}

export function CompanyOpeningStatusAlert({
  currentStep,
  cardStatus,
  onOpenDialog,
  ...props
}: CompanyOpeningStatusAlertProps) {
  const hasFinishedProcess = cardStatus === 'completed'
  return (
    <Accordion
      disableGutters
      sx={{ p: 0, borderRadius: 1, '&::before': { display: 'none' } }}
      {...props}
    >
      <AccordionSummary>
        <Box display="flex" alignItems="center" gap={2}>
          {hasFinishedProcess ? <BurstIcon /> : <PulseIndicator size={14} />}
          <Typography>
            {CARD_STATUS_TITLE[cardStatus]}
          </Typography>
        </Box>
      </AccordionSummary>

      {!hasFinishedProcess && (
        <AccordionDetails>
          <Typography variant="h6" lineHeight={1.2}>
            {currentStep.step}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentStep.status}
          </Typography>
        </AccordionDetails>
      )}

      <AccordionActions sx={{ justifyContent: 'center' }}>
        <Button variant="text" size="small" onClick={onOpenDialog}>
          Ver processo completo
        </Button>
      </AccordionActions>
    </Accordion>
  )
}

const popIn = keyframes`
  0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
  55%  { transform: scale(1.25) rotate(6deg); opacity: 1; }
  80%  { transform: scale(0.92) rotate(-3deg); }
  100% { transform: scale(1) rotate(0deg); }
`

const particleMove = keyframes`
  0%   { transform: translateY(0) scale(1); opacity: 1; }
  70%  { opacity: 1; }
  100% { transform: translateY(-26px) scale(0); opacity: 0; }
`

const ringExpand = keyframes`
  0%   { transform: scale(0.2); opacity: 0.7; }
  100% { transform: scale(2.6); opacity: 0; }
`

const PARTICLE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315]

function BurstIcon() {
  const theme = useTheme()

  const colors = [
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.primary.main,
    theme.palette.success.light,
    theme.palette.warning.light,
    theme.palette.primary.light,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]

  return (
    <Box
      position="relative"
      width={24}
      height={24}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: `2px solid ${theme.palette.success.main}`,
          animation: `${ringExpand} 0.9s ease-out 0.25s both`,
        }}
      />

      {PARTICLE_ANGLES.map((angle, i) => (
        <Box
          key={angle}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 0,
            height: 0,
            transform: `rotate(${angle}deg)`,
          }}
        >
          <Box
            sx={{
              width: i % 2 === 0 ? 4 : 3,
              height: i % 2 === 0 ? 4 : 3,
              borderRadius: '50%',
              bgcolor: colors[i],
              ml: i % 2 === 0 ? '-2px' : '-1.5px',
              mt: i % 2 === 0 ? '-2px' : '-1.5px',
              animation: `${particleMove} 0.85s ease-out both`,
              animationDelay: `${0.25 + (i % 3) * 0.06}s`,
            }}
          />
        </Box>
      ))}

      <CheckCircleIcon
        sx={{
          color: 'success.main',
          fontSize: 24,
          animation: `${popIn} 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.18s both`,
          position: 'relative',
          zIndex: 1,
        }}
      />
    </Box>
  )
}
